import { defaultSections } from "../sidebar";
import type { Bookmark, Huddle, Channel, CustomEmoji, Group, Message, ModalView, Principal, QuietHours, Reminder, ScheduledMessage, SidebarLayout, SidebarSection, Task, UUID } from "./types";

export interface ChannelState {
  channel: Channel;
  // lastSeq is the newest seq applied, with nothing missing before it.
  lastSeq: number;
  // Messages are kept once the channel's history has been loaded.
  loaded: boolean;
  // failed is set while the last try at loading the history failed.
  failed?: boolean;
  // memberChanges counts the joins and leaves heard of, so that what shows
  // the channel's members can look again.
  memberChanges?: number;
  hasMoreHistory: boolean;
  messages: Map<UUID, Message>;
}

export interface PendingMessage {
  clientMsgID: string;
  channelID: UUID;
  threadRootID: UUID | null;
  text: string;
  fileIDs: UUID[];
  alsoInChannel?: boolean;
  status: "sending" | "failed";
  createdAt: number;
}

export type Connection = "connecting" | "online" | "offline" | "signed_out";

export interface State {
  version: number;
  me: Principal | null;
  // workspace is the name of the workspace one is signed in to.
  workspace: string;
  // recallSeconds is how long after sending one may recall a message; 0
  // leaves recalling to admins.
  recallSeconds: number;
  // channelCreation is who may create channels: everyone but guests, or
  // only admins.
  channelCreation: "everyone" | "admins";
  connection: Connection;
  channels: Map<UUID, ChannelState>;
  // Replies of threads that have been opened, by root message ID.
  threads: Map<UUID, Map<UUID, Message>>;
  pending: Map<string, PendingMessage>;
  // Who is typing, by channel: principal ID to when it stops showing.
  typing: Map<UUID, Map<UUID, number>>;
  // Who is online: principal ID to "active" or "away".
  presence: Map<UUID, string>;
  // Tasks being shown, by ID, kept current by task events.
  tasks: Map<UUID, Task>;
  // taskNotices counts personal task events, so task lists know to reload.
  taskNotices: number;
  // reminders is one's reminders not yet done, once loaded; due ones first.
  reminders: Reminder[] | null;
  // scheduled is one's messages waiting to send, once loaded.
  scheduled: ScheduledMessage[] | null;
  // Ephemeral messages shown to this person only, by channel; never stored,
  // so gone on reload.
  ephemeral: Map<UUID, Message[]>;
  // Modals apps opened for this person, the top one last.
  modals: ModalView[];
  // artifactBumps counts artifact changes per channel, so views reload.
  artifactBumps: Map<UUID, number>;
  // toasts are pop-ups for DMs and mentions in channels not in view.
  toasts: Toast[];
  // pausedUntil, while in the future, silences notifications (all devices).
  pausedUntil: string | null;
  // quietHours pause notifications every day at set times.
  quietHours: QuietHours | null;
  // keywords notify as a mention would, wherever they are said.
  keywords: string[];
  // profileBumps counts directory changes, so names and statuses redraw.
  profileBumps: number;
  // drafts is the channels with an unsent draft, for the sidebar.
  drafts: Set<UUID>;
  // unreadThreads counts followed threads with replies not yet read;
  // threadNotices counts thread.activity events, so the Threads view reloads.
  unreadThreads: number;
  threadNotices: number;
  // passwordRequestNotices counts changes to the asks for a password link
  // waiting for admins, so their banner and People list reload.
  passwordRequestNotices: number;
  // saved is the messages one saved for later; savedNotices counts changes,
  // so the Saved page reloads.
  saved: Set<UUID>;
  savedNotices: number;
  // pins is each loaded channel's pinned messages: message ID to who pinned it.
  pins: Map<UUID, Map<UUID, UUID>>;
  // bookmarks is each loaded channel's bookmarks, oldest first.
  bookmarks: Map<UUID, Bookmark[]>;
  // huddles is the calls going on in one's channels, by channel; huddlesOn,
  // whether the server has a media server for them at all.
  huddles: Map<UUID, Huddle>;
  huddlesOn: boolean;
  // ringing is a DM's call one is being rung for, from whom.
  ringing: { channel: UUID; from: UUID } | null;
  // groups is the workspace's user groups, for mentions and the People page.
  groups: Group[];
  // emoji is the workspace's custom emoji, by name.
  emoji: CustomEmoji[];
  // sidebar is how the person arranged their sidebar's sections.
  sidebar: SidebarSection[];
  // sidebarSort and sidebarShow are how Channels and Direct messages sort,
  // and whether they list only what has something unread.
  sidebarSort: SidebarLayout["sort"];
  sidebarShow: SidebarLayout["show"];
  // sidebarShowArchived lists archived channels too; they are left out
  // unless one asks.
  sidebarShowArchived: boolean;
}

export interface Toast {
  id: string;
  // Clicking it opens the message, or the inbox for a reminder of one's own.
  channelID?: UUID;
  messageID?: UUID;
  title: string;
  body: string;
}

export function emptyState(): State {
  return {
    version: 0,
    me: null,
    workspace: "",
    recallSeconds: 120,
    channelCreation: "everyone",
    connection: "connecting",
    channels: new Map(),
    threads: new Map(),
    pending: new Map(),
    typing: new Map(),
    presence: new Map(),
    tasks: new Map(),
    taskNotices: 0,
    reminders: null,
    scheduled: null,
    ephemeral: new Map(),
    modals: [],
    artifactBumps: new Map(),
    toasts: [],
    pausedUntil: null,
    quietHours: null,
    keywords: [],
    profileBumps: 0,
    drafts: new Set(),
    unreadThreads: 0,
    threadNotices: 0,
    passwordRequestNotices: 0,
    saved: new Set(),
    savedNotices: 0,
    pins: new Map(),
    bookmarks: new Map(),
    huddles: new Map(),
    huddlesOn: false,
    ringing: null,
    groups: [],
    emoji: [],
    sidebar: defaultSections(),
    sidebarSort: "name",
    sidebarShow: "all",
    sidebarShowArchived: false,
  };
}

// Store holds client state. Changes go through update(), which makes a new
// snapshot object so React's useSyncExternalStore sees them.
export class Store {
  private state = emptyState();
  private listeners = new Set<() => void>();

  getSnapshot = () => this.state;

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  update(fn: (s: State) => void) {
    fn(this.state);
    this.state = { ...this.state, version: this.state.version + 1 };
    for (const l of this.listeners) l();
  }
}

// sortedMessages returns a channel's top-level messages, oldest first.
export function sortedMessages(cs: ChannelState): Message[] {
  return [...cs.messages.values()].filter((m) => !m.thread_root_id || m.also_in_channel).sort((a, b) => a.seq - b.seq);
}

export function sortedReplies(s: State, root: UUID): Message[] {
  return [...(s.threads.get(root)?.values() ?? [])].sort((a, b) => a.seq - b.seq);
}

// What a channel shows as waiting, by the member's notify level: a DM counts
// each unread message unless muted; a channel counts mentions, muted or not.
export function waitingCount(c: Channel): number {
  const m = c.membership;
  if (!m) return 0;
  const dm = c.kind === "dm" || c.kind === "group_dm";
  if (dm) return m.notify_level === "none" ? 0 : m.unread_count;
  return m.mention_count;
}

// clearTo drops what one cleared from a conversation's history, up to cut
// (ADR-0024): its messages, thread replies and pins (read again when next
// shown). Once anything was dropped, what was loaded reached the cut, so
// there is nothing older to load.
export function clearTo(s: State, channel: UUID, cut: number) {
  const cs = s.channels.get(channel);
  if (!cs) return;
  const m = cs.channel.membership;
  if (m) m.cleared_seq = Math.max(m.cleared_seq ?? 0, cut);
  let dropped = false;
  for (const [id, msg] of cs.messages)
    if (msg.seq <= cut) {
      cs.messages.delete(id);
      dropped = true;
    }
  if (dropped) cs.hasMoreHistory = false;
  for (const replies of s.threads.values())
    for (const [id, msg] of replies) if (msg.channel_id === channel && msg.seq <= cut) replies.delete(id);
  s.pins.delete(channel);
}

// isClosed is whether one closed a DM and no message has come in since.
export function isClosed(c: Channel): boolean {
  const at = c.membership?.closed_at;
  return !!at && !(c.last_message_at && Date.parse(c.last_message_at) > Date.parse(at));
}

// looksUnread is whether the sidebar shows a channel as unread: any message
// when the member hears about all of them, only mentions when only those,
// and never when muted.
export function looksUnread(c: Channel): boolean {
  const m = c.membership;
  if (!m || m.notify_level === "none") return false;
  if (m.notify_level === "mentions") return m.mention_count > 0;
  return m.unread_count > 0;
}
